/**
 * VerticalZonesPrimitive — paints full-pane-height vertical colour bands, one
 * per time slot, used to tint an indicator pane's background by a regime
 * (e.g. Trend Combined above zero = bullish tint, below = bearish tint).
 *
 * Like VerticalLinePrimitive it draws in bitmap space using the pane's full
 * height, so the band spans the whole pane regardless of the price scale. It
 * fills the rectangle between each point's x and the next point's x with the
 * left point's colour (opacity baked in), creating contiguous vertical zones.
 *
 * Time units: UNIX seconds (IST-shifted, matching the chart's time axis).
 */
import type {
  ISeriesPrimitive,
  IPrimitivePaneView,
  IPrimitivePaneRenderer,
  SeriesAttachedParameter,
  Time,
  IChartApi,
  ISeriesApi,
  SeriesType,
} from 'lightweight-charts';
import type { CanvasRenderingTarget2D } from 'fancy-canvas';

export interface VerticalZonePoint {
  time: number;   // UNIX seconds, IST-shifted (same scale as candle data)
  color: string;  // CSS rgba — opacity already baked in
}

class ZonesRenderer implements IPrimitivePaneRenderer {
  constructor(
    private readonly points: VerticalZonePoint[],
    private readonly chart: IChartApi,
  ) {}

  draw(target: CanvasRenderingTarget2D): void {
    const pts = this.points;
    if (pts.length < 2) return;
    const ts = this.chart.timeScale();

    target.useBitmapCoordinateSpace(({ context: ctx, bitmapSize, horizontalPixelRatio: hpr }) => {
      const h = bitmapSize.height;
      const xs: Array<number | null> = pts.map(p => {
        const x = ts.timeToCoordinate(p.time as Time);
        return x == null ? null : Number(x) * hpr;
      });
      // Only the points that resolved to a coordinate.
      const idx: number[] = [];
      for (let i = 0; i < pts.length; i++) if (xs[i] != null) idx.push(i);
      if (idx.length < 2) return;

      // Cell edges = midpoints to the neighbouring bar so cells tile EXACTLY
      // (no overlap → no darker seam, no gap → no lighter seam).
      const cx = (k: number) => xs[idx[k]] as number;
      const leftEdge = (k: number) => k === 0 ? cx(0) - (cx(1) - cx(0)) / 2 : (cx(k - 1) + cx(k)) / 2;
      const rightEdge = (k: number) => k === idx.length - 1 ? cx(k) + (cx(k) - cx(k - 1)) / 2 : (cx(k) + cx(k + 1)) / 2;

      // Merge consecutive SAME-colour cells into one rectangle, so there are no
      // internal boundaries within a colour run — only the real colour changes
      // produce an edge. This removes the per-candle vertical lines.
      let runStart = 0;
      for (let k = 1; k <= idx.length; k++) {
        const atEnd = k === idx.length;
        if (atEnd || pts[idx[k]].color !== pts[idx[runStart]].color) {
          const L = leftEdge(runStart);
          const R = rightEdge(k - 1);
          if (R > L) { ctx.fillStyle = pts[idx[runStart]].color; ctx.fillRect(L, 0, R - L, h); }
          runStart = k;
        }
      }
    });
  }
}

class ZonesPaneView implements IPrimitivePaneView {
  constructor(private readonly parent: VerticalZonesPrimitive) {}
  zOrder() { return 'bottom' as const; } // behind series lines / fills
  renderer(): IPrimitivePaneRenderer | null {
    const chart = this.parent.chart;
    if (!chart) return null;
    return new ZonesRenderer(this.parent.points, chart);
  }
}

export class VerticalZonesPrimitive implements ISeriesPrimitive<Time> {
  points: VerticalZonePoint[] = [];
  chart: IChartApi | null = null;
  series: ISeriesApi<SeriesType> | null = null;
  private readonly _paneViews: ZonesPaneView[];

  constructor(points: VerticalZonePoint[]) {
    this.points = points;
    this._paneViews = [new ZonesPaneView(this)];
  }

  attached(params: SeriesAttachedParameter<Time>): void {
    this.chart = params.chart as IChartApi;
    this.series = params.series as ISeriesApi<SeriesType>;
  }
  detached(): void {
    this.chart = null;
    this.series = null;
  }

  updateAllViews(): void { /* renderer pulls fresh from this.points */ }
  paneViews(): readonly IPrimitivePaneView[] { return this._paneViews; }

  setData(points: VerticalZonePoint[]): void {
    this.points = points;
    this.chart?.applyOptions({});
  }
}
